import React from "react";
import { Link } from "react-router-dom";
import { Section } from "./ui/Section";
import { DetailList, DetailRow } from "./ui/Cards";
import { LoadingState } from "./ui/AsyncState";
import { useCurrentUser } from "./utils/useCurrentUser";
import { getUserCategory } from "./utils/getUserCategory";

/**
 * O aplikaci (/about). Titulek a tlačítko Zpět dodává AppShell (Screen
 * v App.tsx). Údaje účtu se čtou přes useCurrentUser – dřívější synchronní
 * auth.currentUser byl po obnovení stránky null a stav členství se neukázal.
 */

import { CONTACT_EMAIL } from "./utils/contact";
const APP_URL = "https://lks-apka.netlify.app/";

// Čas sestavení a commit doplňuje skript prebuild do .env (jako Přehled systému).
const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || "neznámá";
const COMMIT_HASH = process.env.REACT_APP_COMMIT_HASH || "neznámý";

interface InfoItem {
  icon: string;
  title: string;
  text: React.ReactNode;
}

const HOW_IT_WORKS: InfoItem[] = [
  {
    icon: "bi-credit-card",
    title: "Automatické zpracování plateb",
    text: "Platby z banky se párují automaticky – přes den do 15 minut od připsání na účet, v noci do hodiny. O víkendech a svátcích banka avíza posílá se zpožděním.",
  },
  {
    icon: "bi-people",
    title: "Výhodné startovné pro členy",
    text: "Pokud jste členem LKS a máte zaplacený členský příspěvek, systém to automaticky rozpozná podle shody e-mailu a přiřadí vám status člena. Členové mají nárok na zvýhodněné startovné a jsou automaticky zařazeni do výsledkového systému.",
  },
  {
    icon: "bi-trophy",
    title: "Výsledky",
    text: "Ve výsledcích se zobrazují pouze střelci registrovaní přes aplikaci, kteří mají přidělené číslo uživatele. Tím je zajištěna konzistence a jednoznačné párování výkonů.",
  },
  {
    icon: "bi-phone",
    title: "Použití aplikace",
    text: (
      <>
        Aplikaci můžete používat na počítači na adrese{" "}
        <a href={APP_URL} target="_blank" rel="noopener noreferrer">
          lks-apka.netlify.app
        </a>
        , nebo si ji stáhnout jako mobilní aplikaci pro Android nebo iPhone.
      </>
    ),
  },
];

/** Textová hodnota pole dokumentu, nebo null pro prázdné / jiné typy. */
const text = (raw: unknown): string | null => {
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  if (typeof raw === "string" && raw.trim() !== "") return raw.trim();
  return null;
};

const AboutApp: React.FC = () => {
  const { userDoc, loading } = useCurrentUser();
  const year = new Date().getFullYear();

  // Stav členství = první pravidlo kategorie (zaplaceno na letošní rok);
  // věková hranice juniora tu nehraje roli.
  const isMember = userDoc
    ? getUserCategory({
        membership: userDoc.membership,
        juniorMaxAge: null,
        year,
      }) !== "nečlen"
    : false;
  const sequenceNumber = userDoc ? text(userDoc.sequenceNumber) : null;

  return (
    <>
      <Section title="O projektu" icon="bi-info-circle">
        <p>
          Tato aplikace vznikla za účelem zjednodušení přihlašování střelců na
          závody a digitalizace veškeré agendy kolem registrace, plateb
          a výsledků. Věříme, že moderní způsob registrace přináší nejen větší
          komfort střelcům, ale i přehlednější a efektivnější správu
          pořadatelům.
        </p>
        <p>
          Aplikace byla vytvořena pro LKS a{" "}
          <strong>stále se aktivně vyvíjí</strong>. Cílem je co nejvíce
          zjednodušit přihlašování všem účastníkům závodů. Budu moc rád za
          jakoukoliv zpětnou vazbu nebo návrhy na zlepšení.
        </p>
        <p className="mb-0 text-end text-body-secondary">JaVi 2026</p>
      </Section>

      <Section title="Jak to funguje" icon="bi-lightbulb">
        <ul className="list-unstyled mb-0">
          {HOW_IT_WORKS.map((item, index) => (
            <li
              key={item.title}
              className={
                index < HOW_IT_WORKS.length - 1 ? "d-flex mb-3" : "d-flex"
              }
            >
              <i
                className={`bi ${item.icon} me-3`}
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  fontSize: "1.25rem",
                  lineHeight: 1.2,
                  color: "var(--lks-brand, #004d40)",
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div className="fw-semibold">{item.title}</div>
                <div>{item.text}</div>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {loading || userDoc ? (
        <Section title="Váš účet" icon="bi-person-badge">
          {loading ? (
            <LoadingState inline text="Načítám údaje…" />
          ) : (
            <DetailList>
              <DetailRow
                label="Stav členství"
                value={
                  isMember ? (
                    <span className="text-success">
                      <i
                        className="bi bi-check-circle-fill me-1"
                        aria-hidden="true"
                      />
                      Člen – zaplaceno na rok {year}
                    </span>
                  ) : (
                    <span className="text-danger">
                      <i
                        className="bi bi-x-circle-fill me-1"
                        aria-hidden="true"
                      />
                      Nečlen
                    </span>
                  )
                }
              />
              <DetailRow
                label="Číslo uživatele"
                value={
                  sequenceNumber ?? (
                    <span className="lks-detailrow__empty">Nepřiděleno</span>
                  )
                }
              />
            </DetailList>
          )}
        </Section>
      ) : null}

      <Section title="Kontakt" icon="bi-envelope">
        <p>
          Máte návrh nebo chcete nahlásit chybu? Napište nám na{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
        <p className="mb-0">
          Podmínky používání a zásady ochrany osobních údajů naleznete na
          stránce <Link to="/gdpr">Ochrana osobních údajů</Link>.
        </p>
      </Section>

      <Section title="Verze aplikace" icon="bi-code-slash">
        <DetailList>
          <DetailRow label="Verze" value={BUILD_TIME} />
          <DetailRow label="Sestavení" value={COMMIT_HASH} />
        </DetailList>
      </Section>
    </>
  );
};

export default AboutApp;
